import { useState, useEffect } from 'react';
import {
	Modal,
	Stack,
	Tabs,
	Button,
	Group,
	Text,
	Paper,
	Box,
	LoadingOverlay,
} from '@mantine/core';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { IconGripVertical } from '@tabler/icons-react';
import { useGetAllWorkoutCategoriesQuery, useGetAllWorkoutsQuery } from '@codezo/graphql';

interface WorkoutReorderModalProps {
	opened: boolean;
	onClose: () => void;
	onSave: (orderedWorkouts: Array<{ id: string; sortOrder: number; categoryId: string }>) => void;
}

export function WorkoutReorderModal({ opened, onClose, onSave }: WorkoutReorderModalProps) {
	const [activeTab, setActiveTab] = useState<string | null>(null);
	const [workoutsByCategory, setWorkoutsByCategory] = useState<Record<string, any[]>>({});

	const { data: categoriesData, loading: categoriesLoading } = useGetAllWorkoutCategoriesQuery();
	const { data: workoutsData, loading: workoutsLoading, refetch: refetchWorkouts } = useGetAllWorkoutsQuery({
		variables: { categoryId: null }, // Get all workouts
	});

	// Refetch workouts when modal opens
	useEffect(() => {
		if (opened) {
			refetchWorkouts();
		}
	}, [opened, refetchWorkouts]);

	// Initialize workouts grouped by category
	useEffect(() => {
		if (workoutsData?.getAllWorkouts && categoriesData?.getAllWorkoutCategories) {
			const grouped: Record<string, any[]> = {};

			// Group workouts by category
			categoriesData.getAllWorkoutCategories
				.filter((cat) => cat.isActive)
				.forEach((category) => {
					const categoryWorkouts = workoutsData.getAllWorkouts
						.filter((w) => w.categoryId === category.id)
						.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
					grouped[category.id] = categoryWorkouts;
				});

			setWorkoutsByCategory(grouped);

			// Set first category as active
			if (!activeTab && categoriesData.getAllWorkoutCategories.length > 0) {
				setActiveTab(categoriesData.getAllWorkoutCategories[0].id);
			}
		}
	}, [workoutsData, categoriesData, activeTab]);

	const handleDragEnd = (result: DropResult) => {
		if (!result.destination || !activeTab) return;

		const categoryWorkouts = [...workoutsByCategory[activeTab]];
		const [reorderedItem] = categoryWorkouts.splice(result.source.index, 1);
		categoryWorkouts.splice(result.destination.index, 0, reorderedItem);

		setWorkoutsByCategory({
			...workoutsByCategory,
			[activeTab]: categoryWorkouts,
		});
	};

	const handleSave = () => {
		const orderedWorkouts: Array<{ id: string; sortOrder: number; categoryId: string }> = [];

		// Flatten all workouts with their new sort order
		Object.entries(workoutsByCategory).forEach(([categoryId, workouts]) => {
			workouts.forEach((workout, index) => {
				orderedWorkouts.push({
					id: workout.id,
					sortOrder: index + 1,
					categoryId,
				});
			});
		});

		onSave(orderedWorkouts);
	};

	const categories = categoriesData?.getAllWorkoutCategories?.filter((cat) => cat.isActive) || [];
	const loading = categoriesLoading || workoutsLoading;

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			size="xl"
			title="Reorder Workouts"
			styles={{
				body: { minHeight: '500px' },
			}}
		>
			<LoadingOverlay visible={loading} />

			<Stack gap="md">
				<Text size="sm" c="dimmed">
					Drag and drop workouts to reorder them within each category
				</Text>

				<Tabs value={activeTab} onChange={setActiveTab}>
					<Tabs.List>
						{categories.map((category) => (
							<Tabs.Tab key={category.id} value={category.id}>
								{category.name}
							</Tabs.Tab>
						))}
					</Tabs.List>

					{categories.map((category) => (
						<Tabs.Panel key={category.id} value={category.id} pt="md">
							<DragDropContext onDragEnd={handleDragEnd}>
								<Droppable droppableId={category.id}>
									{(provided) => (
										<Stack
											gap="xs"
											{...provided.droppableProps}
											ref={provided.innerRef}
											style={{ minHeight: '300px' }}
										>
											{workoutsByCategory[category.id]?.length > 0 ? (
												workoutsByCategory[category.id].map((workout, index) => (
													<Draggable
														key={workout.id}
														draggableId={workout.id}
														index={index}
													>
														{(provided, snapshot) => (
															<Paper
																ref={provided.innerRef}
																{...provided.draggableProps}
																withBorder
																p="md"
																style={{
																	...provided.draggableProps.style,
																	backgroundColor: snapshot.isDragging
																		? '#f8f9fa'
																		: 'white',
																	cursor: 'grab',
																	left: 'auto !important',
																	top: 'auto !important',
																}}
															>
																<Group gap="sm" wrap="nowrap">
																	<Box
																		{...provided.dragHandleProps}
																		style={{ cursor: 'grab' }}
																	>
																		<IconGripVertical
																			size={20}
																			color="#868e96"
																		/>
																	</Box>
																	<Stack gap={4} style={{ flex: 1 }}>
																		<Group gap="xs">
																			<Text size="sm" fw={500}>
																				{index + 1}.
																			</Text>
																			<Text size="sm" fw={500}>
																				{workout.name}
																			</Text>
																		</Group>
																		<Text size="xs" c="dimmed">
																			{workout.type} • {workout.intensity}
																		</Text>
																	</Stack>
																</Group>
															</Paper>
														)}
													</Draggable>
												))
											) : (
												<Text size="sm" c="dimmed" ta="center" py="xl">
													No workouts in this category
												</Text>
											)}
											{provided.placeholder}
										</Stack>
									)}
								</Droppable>
							</DragDropContext>
						</Tabs.Panel>
					))}
				</Tabs>

				<Group justify="flex-end" mt="md">
					<Button variant="subtle" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={handleSave}>Save Order</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
